import { type Writable, writable } from "svelte/store";
import type { EditorLayout, GridLayout, SubLayoutState } from "../editor";

export function createGridLayout(id: string, editorLayout: EditorLayout) {
    const layout = editorLayout.subLayouts[id] as GridLayout & { state: Writable<SubLayoutState> };
    const { areas: data, fill, resize } = layout;


    const areas = data.rows.map((row) => row.areas);
    const ids = [...new Set(areas.flat())];

    const sizeFill = {
        row: areas.map((row) => !!(row.indexOf(fill) + 1)),
        col: areas[0].map((_, i) => !!areas.find((row) => row[i] === fill)),
    };

    const positions = areas
        .map((row, y) =>
            row.map((id, x) => {
                return { id, x, y };
            })
        )
        .flat()
        .reduce(
            (acc, n) => {
                acc[n.id] = [...(acc[n.id] ?? []), { x: n.x, y: n.y }];
                return acc;
            },
            {} as Record<string, { x: number; y: number }[]>
        );

    const top = (id: string) =>
        !!positions[id].find((pos) => sizeFill.row[pos.y - 1] && areas[pos.y - 1][pos.x] !== id);
    const bottom = (id: string) =>
        !!positions[id].find((pos) => sizeFill.row[pos.y + 1] && areas[pos.y + 1][pos.x] !== id);
    const left = (id: string) =>
        !!positions[id].find((pos) => sizeFill.col[pos.x - 1] && areas[pos.y][pos.x - 1] !== id);
    const right = (id: string) =>
        !!positions[id].find((pos) => sizeFill.col[pos.x + 1] && areas[pos.y][pos.x + 1] !== id);

    const resizeHandles = resize.map((id) => {
        return {
            id,
            top: top(id),
            bottom: bottom(id),
            left: left(id),
            right: right(id),
        };
    });

    const autoSize = [
        [!resizeHandles.some((v) => v.right), !resizeHandles.some((v) => v.left)],
        [!resizeHandles.some((v) => v.bottom), !resizeHandles.some((v) => v.top)],
    ];

    const collapsed = writable([
        [false, false],
        [false, false],
    ] as [[boolean, boolean], [boolean, boolean]]);

    const collapse = (area: string, value?: boolean) => {
        collapsed.update(([hor, ver]) => {
            if (areas[0][1] === area) { // top
                return [hor, [value ?? !ver[0], ver[1]]]
            } else if (areas[2][1] === area) { // bottom
                return [hor, [ver[0], value ?? !ver[1]]];
            } else if (areas[1][0] === area) { // left
                return [[value ?? !hor[0], hor[1]], ver];
            } else if (areas[1][2] === area) { // right
                return [[hor[0], value ?? !hor[1]], ver];
            } else {
                console.error({ areas, area });
                throw new Error(`Invalid area: ${area}`);
            }
        });
    };

    const initialSize = [
        [data.columns[0].initial ?? 25, data.columns[2].initial ?? 25],
        [data.rows[0].initial ?? 25, data.rows[2].initial ?? 25],
    ];

    const state = editorLayout.state.create(id, {
        size: initialSize
    });

    return {
        ids,
        areas,
        resize: resizeHandles,
        minSize: [data.columns.map((col) => col.min ?? 32), data.rows.map((row) => row.min ?? 32)],
        autoSize,
        collapsed,
        collapse,

        state,
    };
}
